/* eslint-disable no-underscore-dangle */
import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type { LitterListGetBreedersQuery, LitterListGetBreedersQueryVariables } from '@gql/graphql';
import { useMemo } from 'react';
import { useBreederOrderBuilder } from '../../../BreederListPageController/api/useBreederOrderBuilder';
import { useSearch } from '../../useSearch';
import { useBreederWhereBuilder } from './useBreederWhereBuilder';

export const LITTER_LIST_GET_BREEDERS = graphql(`
  query LitterListGetBreeders(
    $where: DogBreederWhereInput
    $orderBy: [DogBreederOrderByWithRelationAndSearchRelevanceInput!]
    $take: Int
    $breederCardInfoTranslationsWhere: DogBreederTranslationWhereInput
    $breederCardInfoTranslationsWhereBreed: DogBreedTranslationWhereInput
    $breederCardInfoBreedImgsTake: Int
    $breederCardInfoLittersWhere: LitterWhereInput
  ) {
    findManyDogBreeder(where: $where, orderBy: $orderBy, take: $take) {
      ...BreederCardInfo
      breederBreeds {
        id
        dogBreed {
          id
          name
          baseBreedName
          imgs(take: $breederCardInfoBreedImgsTake) {
            id
            medium
          }
          translations(where: $breederCardInfoTranslationsWhereBreed) {
            id
            name
          }
        }
      }
    }
    aggregateDogBreeder(where: $where) {
      _count {
        _all
      }
    }
  }
`);

export interface UseGetBreedersParams {
  forceSkip?: boolean;
}

export const useGetBreeders = (params: UseGetBreedersParams) => {
  const { forceSkip = false } = params;

  const { language } = useRouter();
  const { valuesFromSearch } = useSearch();
  const { breederWhereBuilder } = useBreederWhereBuilder();
  const { breederOrderBuilder } = useBreederOrderBuilder();

  const variables: LitterListGetBreedersQueryVariables = useMemo(() => {
    return {
      where: breederWhereBuilder(valuesFromSearch),
      orderBy: breederOrderBuilder(),
      breederCardInfoTranslationsWhere: { language: { equals: language } },
      breederCardInfoTranslationsWhereBreed: { language: { equals: language } },
      breederCardInfoBreedImgsTake: 1,
      breederCardInfoLittersWhere: {
        archived: { equals: false },
        adminValidated: { equals: true },
      },
    } as LitterListGetBreedersQueryVariables;
  }, [breederWhereBuilder, breederOrderBuilder, language, valuesFromSearch]);

  const { data, error, loading, fetchMore, refetch } = useQuery<
    LitterListGetBreedersQuery,
    LitterListGetBreedersQueryVariables
  >(LITTER_LIST_GET_BREEDERS, {
    variables,
    notifyOnNetworkStatusChange: true,
    skip: forceSkip,
  });

  return {
    breeders: data?.findManyDogBreeder ?? [],
    count: data?.aggregateDogBreeder?._count?._all ?? 0,
    // eslint-disable-next-line no-underscore-dangle
    breedersError: error,
    breedersLoading: loading,
    breedersVariables: variables,
    breedersFetchMore: fetchMore,
    breedersRefetch: refetch,
  };
};
